import React, { useEffect, useRef } from 'react';
import {
  selectGetRedirectTransactionStatus,
  selectTransactionStatus,
  selectRedirectTransaction,
  selectTransaction,
  selectPatchTransactionsStatus,
} from 'store/transactions/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { ACTION_STATUSES, PATCH_STATUSES_3DS, QUERY_PARAMS } from 'shared/consts';
import { getRedirectTransaction, getTransaction, patchTransaction } from 'store/transactions/actions';
import { replace } from 'store/router/actions';
import { useLocation } from 'react-router-dom';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { useCart } from 'hooks/use-cart.hook';
import { useFlags } from 'launchdarkly-react-client-sdk';
import qs from 'qs';
import { pick } from 'utils/pickObjectProperty';
import { Skeleton } from 'components/skeleton';
import { useBookingCompletionSegment } from 'hooks/use-booking-completion-segment.hook';
import { BOOKING_COMPLETED_SEGMENT } from 'shared/consts/paths';
import { useTimeout } from 'hooks/use-timeout.hook';
import { usePostBookingLanding } from 'hooks/use-post-booking-landing.hook';

/* RES-1407: how long the 3DS return may wait before landing on My bookings regardless. */
export const REDIRECT_WAIT_MS = 20000;

/* The failure screen a failed 3DS lands on, without the slot's time params. */
const transactionFailedPath = (params: Record<string, unknown>): string =>
  `/transaction-failed?${qs.stringify(
    pick(
      params,
      QUERY_PARAMS.filter(
        param =>
          param !== 'startTime' &&
          param !== 'startDate' &&
          param !== 'endTime' &&
          param !== 'isExact' &&
          param !== 'duration',
      ),
    ),
  )}`;

/*
 * RES-1407 — under the 2-way chat redesign a 3DS return lands STRAIGHT ON MY BOOKINGS with the booking
 * selected: no `booked` step, so no add-on offer and no second wait. The tenant adds add-ons from the
 * booking's details there. (Mason, 2026-10-21: the in-flow add-on step does not survive the full-page
 * 3DS redirect; the non-3DS flows keep it.)
 *
 * ⛔ NOT VIA THE ROOT. This page is reached by a full page load (the gateway sends the frame back to
 * `/redirect`), so it has no resource page under it, and at the root `/*` is ResourcesPage
 * (`resource-booking-skip-landing-page`), which fetches nothing away from `/` and so never leaves its
 * Skeleton. My bookings is a top-level route with no such dependency.
 *
 * ⛔ IT WAITS FOR THE SUCCESS PATCH. That PATCH is what turns the `Pending_Payment` booking into a real
 * one; landing before it answers would show the booking as pending payment until the next refresh.
 */
export const RedirectPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname } = location;
  const { cartId, transaction_token, ...restQueryParams } = useSearchParams();
  const { isCartFulfilled, cart } = useCart(cartId as string);
  const getRedirectTransactionStatus = useSelector(selectGetRedirectTransactionStatus);
  const redirectTransaction = useSelector(selectRedirectTransaction);
  const { showMiniappPaymentsNavigation } = useFlags();
  const transactionStatus = useSelector(selectTransactionStatus);
  const transaction = useSelector(selectTransaction);
  const patchTransactionsStatus = useSelector(selectPatchTransactionsStatus);
  /* RES-1407: `receipt` with the flag off, `booked` (no receipt) with it on. */
  const completionSegment = useBookingCompletionSegment();
  const landsOnMyBookings = completionSegment === BOOKING_COMPLETED_SEGMENT;
  const landOnMyBookings = usePostBookingLanding();
  /* Set once the success PATCH is sent under the redesign: its answer then means "land", not "failed". */
  const awaitsSuccessPatch = useRef(false);
  const hasLanded = useRef(false);

  const landOnBooking = () => {
    if (hasLanded.current) {
      return;
    }
    hasLanded.current = true;
    landOnMyBookings({
      transactionUuid: cart?.transaction_uuid ?? undefined,
      bookingId: transaction?.uuid === cart?.transaction_uuid ? transaction?.details?.id : undefined,
      params: restQueryParams,
    });
  };

  /*
   * `isMarkedFailed`: helix answered the FAILED patch, so the booking now reads "Payment failed" and the
   * redesign lands on it like any other (Mason, 2026-10-23: no same-cart retry). Until then it still
   * reads as pending payment, so a stalled patch goes to the failure screen instead.
   */
  const leaveOnFailure = (isMarkedFailed: boolean) => {
    if (landsOnMyBookings && isMarkedFailed) {
      landOnBooking();
      return;
    }
    dispatch(replace(transactionFailedPath(restQueryParams)));
  };

  useEffect(() => {
    if (transaction_token) {
      dispatch(getRedirectTransaction.request(transaction_token as string));
    }
  }, [dispatch, transaction_token]);

  useEffect(() => {
    if (showMiniappPaymentsNavigation && cart?.transaction_uuid) {
      dispatch(getTransaction.request(cart.transaction_uuid));
    }
  }, [cart?.transaction_uuid, dispatch, showMiniappPaymentsNavigation]);

  useEffect(() => {
    if (awaitsSuccessPatch.current) {
      /* Either answer lands: a rejected success PATCH still leaves a booking, shown in its real state. */
      if (
        patchTransactionsStatus === ACTION_STATUSES.FULFILLED ||
        patchTransactionsStatus === ACTION_STATUSES.REJECTED
      ) {
        landOnBooking();
      }
      return;
    }

    if (patchTransactionsStatus === ACTION_STATUSES.FULFILLED) {
      leaveOnFailure(true);
    }
  }, [restQueryParams, patchTransactionsStatus]);

  useEffect(() => {
    if (
      getRedirectTransactionStatus === ACTION_STATUSES.FULFILLED &&
      isCartFulfilled &&
      cart?.transaction_uuid &&
      transactionStatus === ACTION_STATUSES.FULFILLED
    ) {
      if (redirectTransaction?.succeeded) {
        if (showMiniappPaymentsNavigation && transactionStatus === ACTION_STATUSES.FULFILLED) {
          if (awaitsSuccessPatch.current) {
            return;
          }
          awaitsSuccessPatch.current = landsOnMyBookings;
          dispatch(
            patchTransaction.request({
              transactionId: transaction?.id.toString(),
              status_3ds: PATCH_STATUSES_3DS.SUCCEEDED,
              cartId: cartId as string,
            }),
          );

          if (!landsOnMyBookings) {
            dispatch(replace(`${pathname.replace('redirect', completionSegment)}?${qs.stringify(restQueryParams)}`));
          }
        }
      } else {
        dispatch(
          patchTransaction.request({
            transactionId: transaction?.id.toString(),
            status_3ds: PATCH_STATUSES_3DS.FAILED,
            cartId: cartId as string,
          }),
        );
      }
    }
  }, [
    transactionStatus,
    transaction,
    cart?.transaction_uuid,
    cart?.transaction_id,
    dispatch,
    getRedirectTransactionStatus,
    isCartFulfilled,
    location.search,
    redirectTransaction,
    showMiniappPaymentsNavigation,
    completionSegment,
  ]);

  /*
   * RES-1407 — a bound on this page's wait, under the redesign only. It renders a Skeleton until four
   * reads line up (the 3DS result, the cart, the transaction, its status) and then the success PATCH,
   * and had no way out when one never did. After the bound it lands on My bookings: the booking selected
   * when the cart named it, the upcoming tab otherwise. Flag off keeps the old behaviour exactly.
   *
   * ⛔ A KNOWN FAILURE ONLY LANDS ONCE IT IS MARKED FAILED. When the 3DS result came back unsuccessful the
   * page asks for the failure patch and waits on it; if that stalls, the bound goes to the failure screen.
   */
  const isKnownFailure =
    getRedirectTransactionStatus === ACTION_STATUSES.FULFILLED &&
    Boolean(redirectTransaction) &&
    !redirectTransaction?.succeeded;

  useTimeout(
    () => {
      if (isKnownFailure) {
        leaveOnFailure(false);
      } else {
        landOnBooking();
      }
    },
    landsOnMyBookings ? REDIRECT_WAIT_MS : null,
  );

  return <Skeleton />;
};
